import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useDeviceAuth } from '../hooks/useDeviceAuth';
import { useAuth } from '../contexts/AuthContext';
import { Loader2, Copy, CheckCircle, AlertCircle, Smartphone } from 'lucide-react';
import { toast } from 'sonner';

export default function DeviceAuthPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const {
    isLoading,
    userCode,
    verificationUri,
    verificationUriComplete,
    accessToken,
    error,
    isPolling,
    startDeviceAuth,
    startContinuousPolling,
    reset
  } = useDeviceAuth();

  const [stopPolling, setStopPolling] = useState<(() => void) | null>(null);

  // Start device authorization flow automatically when page loads
  useEffect(() => {
    const initAuth = async () => {
      try {
        const result = await startDeviceAuth('ui-client');
        
        // Start continuous polling for token
        const { stopPolling: stopFn } = await startContinuousPolling(result.device_code);
        setStopPolling(() => stopFn);
      } catch (err) {
        console.error('Failed to start device auth:', err);
      }
    };

    initAuth();

    // Cleanup on unmount
    return () => {
      if (stopPolling) {
        stopPolling();
      }
    };
  }, []);

  // Handle successful authentication
  useEffect(() => {
    if (accessToken) {
      login(accessToken);
      if (stopPolling) {
        stopPolling();
        setStopPolling(null);
      }
      // Navigate to the original destination or home
      navigate('/', { replace: true });
    }
  }, [accessToken, login, navigate, stopPolling]);

  const handleCopyCode = () => {
    if (userCode) {
      navigator.clipboard.writeText(userCode);
      toast.success('User code copied to clipboard');
    }
  };

  const handleCopyUrl = () => {
    if (verificationUriComplete) {
      navigator.clipboard.writeText(verificationUriComplete);
      toast.success('Verification URL copied to clipboard');
    }
  };

  const handleManualAuth = () => {
    if (stopPolling) {
      stopPolling();
      setStopPolling(null);
    }
    navigate('/auth/manual');
  };

  const handleBack = () => {
    if (stopPolling) {
      stopPolling();
      setStopPolling(null);
    }
    reset();
    navigate('/auth');
  };

  const handleClose = () => {
    if (stopPolling) {
      stopPolling();
      setStopPolling(null);
    }
    reset();
    navigate('/welcome');
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Main content - centered */}
      <div className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md mx-auto">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <Smartphone className="h-12 w-12 text-primary" />
            </div>
            <CardTitle className="flex items-center justify-center gap-2">
              {isLoading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Starting Authorization...
                </>
              ) : error ? (
                <>
                  <AlertCircle className="h-5 w-5 text-destructive" />
                  Authorization Error
                </>
              ) : (
                <>
                  Waiting for Authorization
                </>
              )}
            </CardTitle>
            <CardDescription>
              {isLoading 
                ? 'Please wait while we set up device authorization...'
                : error 
                ? 'There was an error starting the authorization process.'
                : 'Please authorize this device using the code below'
              }
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-4">
            {error && (
              <div className="p-3 bg-destructive/10">
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}

            {!isLoading && !error && userCode && (
              <>
                <div className="text-center space-y-3">
                  <Label className="text-sm font-medium">User Code</Label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 text-center">
                      <div className="text-2xl font-mono font-bold bg-muted p-2">
                        {userCode}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCopyCode}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {verificationUriComplete && (
                  <div className="text-center space-y-2">
                    <Label className="text-sm font-medium">Verification URL</Label>
                    <div className="flex items-center gap-2">
                      <Input 
                        value={verificationUriComplete} 
                        readOnly 
                        className="text-xs"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleCopyUrl}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">Or</span>
                  </div>
                </div>

                <Button 
                  variant="outline" 
                  onClick={handleManualAuth}
                  className="w-full"
                >
                  Enter User Code Manually
                </Button>
              </>
            )}

            {isPolling && (
              <div className="text-center text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin mx-auto mb-2" />
                Waiting for authorization... (checking every 30 seconds)
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Bottom actions */}
      <div className="p-6 bg-white border-t">
        <div className="max-w-md mx-auto flex gap-3">
          <Button 
            variant="outline" 
            onClick={handleBack}
            className="flex-1"
          >
            Back
          </Button>
          <Button 
            variant="outline" 
            onClick={handleClose}
            className="flex-1"
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}